/**
 * The game's failure screen (M288): where the game itself cannot go on, the simulation or the interface failing, said
 * plainly over the page, the error with it, and the way back (the autosave kept; reloading opens it). A 3D view that
 * cannot start or stops is not this: the game goes on without it (`main.ts`). Imported first, so an error while the page
 * starts (before `markStarted`) shows it too; after that, only what `main.ts` hands it.
 */
let started = false;
let shown = false;

/** Whether the game has failed: nothing is saved from then on (the world it failed in is not the save to reopen). */
export function hasFailed(): boolean {
  return shown;
}

/** The page has started: errors from here on are `main.ts`'s to judge. */
export function markStarted(): void {
  started = true;
}

const escape = (text: string): string => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/** Shows the failure screen once, over everything, with the error's own words. */
export function showFailure(what: string, error: unknown): void {
  console.error(what, error);
  if (shown) return;
  shown = true;
  const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  const el = document.createElement('div');
  el.className = 'failure';
  el.setAttribute('role', 'alertdialog');
  el.setAttribute('aria-label', what);
  el.innerHTML = `<div class="failure-card">
    <h2>${escape(what)}</h2>
    <p>The game cannot go on from here. Your last autosave is kept, and reloading the page opens it.</p>
    <pre>${escape(detail)}</pre>
    <button type="button">Reload</button>
  </div>`;
  el.querySelector('button')?.addEventListener('click', () => location.reload());
  document.body.appendChild(el);
}

window.addEventListener('error', (event) => {
  if (!started) showFailure('The game could not start', event.error ?? event.message);
});
window.addEventListener('unhandledrejection', (event) => {
  if (!started) showFailure('The game could not start', event.reason);
});
